import { useState, useEffect, useCallback } from 'react';
import { performance as perfApi } from '../../services/api';
import Badge from '../../components/common/Badge';
import Button from '../../components/common/Button';
import Modal from '../../components/common/Modal';
import { theme } from '../../theme';
import { SearchableSelect } from '../../components/common/SearchableSelect';

// ─── Styles ───────────────────────────────────────────────────────────────────

const inp = {
  width: '100%', padding: '0.6rem 0.875rem',
  border: `1.5px solid ${theme.border}`, borderRadius: '0.5rem',
  fontSize: '0.875rem', color: theme.text, outline: 'none',
  boxSizing: 'border-box', background: '#fff',
};
const lbl = { display: 'block', fontSize: '0.82rem', fontWeight: 600, color: theme.text, marginBottom: '0.3rem' };
const fieldRow = { marginBottom: '0.875rem' };
const card = { background: theme.white, border: `1px solid ${theme.border}`, borderRadius: '0.75rem', padding: '1.25rem' };

const DEPARTMENTS = ['Software Engineering', 'Support Division', 'Sales and Presales', 'Back Office'];
const CATEGORIES = {
  'Software Engineering': ['Development', 'Testing', 'Design UI/UX', 'BA', 'DevOps', 'DBA'],
  'Support Division': ['Data Centre', 'Projects', 'IT Support', 'Helpdesk'],
  'Sales and Presales': ['Sales', 'Presales', 'Bid Management'],
  'Back Office': ['HR & Admin', 'Purchase & Accounts'],
};

const STATUS_COLORS = {
  draft: { bg: '#F3F4F6', text: '#6B7280' },
  active: { bg: '#DBEAFE', text: '#1D4ED8' },
  on_track: { bg: '#D1FAE5', text: '#065F46' },
  at_risk: { bg: '#FEF3C7', text: '#92400E' },
  completed: { bg: '#D1FAE5', text: '#065F46' },
  cancelled: { bg: '#FEE2E2', text: '#991B1B' },
};

const COMPETENCY_LEVELS = ['beginner', 'developing', 'proficient', 'advanced', 'expert'];
const COMPETENCY_COLORS = {
  beginner: '#EF4444', developing: '#F97316', proficient: '#F59E0B',
  advanced: '#3B82F6', expert: '#10B981',
};

// ─── Shared Helpers ───────────────────────────────────────────────────────────

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function StatCard({ label, value, color = theme.primary }) {
  return (
    <div style={{ background: theme.white, border: `1px solid ${theme.border}`, borderLeft: `4px solid ${color}`, borderRadius: '0.5rem', padding: '0.75rem 1.25rem', minWidth: 110 }}>
      <div style={{ fontSize: '1.5rem', fontWeight: 700, color }}>{value}</div>
      <div style={{ fontSize: '0.75rem', color: theme.textMuted, marginTop: 2 }}>{label}</div>
    </div>
  );
}

function ProgressBar({ value, color = '#3B82F6' }) {
  const pct = Math.min(100, Math.max(0, value || 0));
  return (
    <div style={{ background: '#F3F4F6', borderRadius: 999, height: 8, overflow: 'hidden' }}>
      <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 999, transition: 'width 0.4s' }} />
    </div>
  );
}

function GoalStatusBadge({ status }) {
  const colors = STATUS_COLORS[status] || STATUS_COLORS.draft;
  return (
    <span style={{ fontSize: '0.72rem', fontWeight: 700, padding: '0.2rem 0.6rem', borderRadius: 999, background: colors.bg, color: colors.text, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
      {status?.replace('_', ' ')}
    </span>
  );
}

// ─── Cycle Progress Bar ───────────────────────────────────────────────────────

function CycleProgress({ submitted, reviewed, total }) {
  if (!total) return <span style={{ fontSize: '0.75rem', color: theme.textMuted }}>No assessments</span>;
  const subPct = Math.round((submitted / total) * 100);
  const revPct = Math.round((reviewed / total) * 100);
  return (
    <div style={{ minWidth: 140 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.68rem', color: theme.textMuted, marginBottom: '0.2rem' }}>
        <span>{submitted}/{total} submitted</span>
        <span>{reviewed} reviewed</span>
      </div>
      <div style={{ height: 6, borderRadius: 3, background: '#E5E7EB', overflow: 'hidden', position: 'relative' }}>
        <div style={{ position: 'absolute', left: 0, top: 0, height: '100%', width: `${subPct}%`, background: '#3B82F6', borderRadius: 3 }} />
        <div style={{ position: 'absolute', left: 0, top: 0, height: '100%', width: `${revPct}%`, background: '#10B981', borderRadius: 3 }} />
      </div>
    </div>
  );
}

// ─── Assessment Detail View ───────────────────────────────────────────────────

function AssessmentDetailView({ a }) {
  const grouped = (a.responses || []).reduce((acc, r) => {
    const sec = r.field_section || 'General';
    if (!acc[sec]) acc[sec] = [];
    acc[sec].push(r);
    return acc;
  }, {});

  const ratingColor = (n) => {
    if (!n) return theme.textMuted;
    if (n >= 8) return '#10B981'; if (n >= 6) return '#3B82F6';
    if (n >= 4) return '#F59E0B'; return '#EF4444';
  };
  const RATING_LABELS = { 1: 'Extremely Poor', 2: 'Very Poor', 3: 'Poor', 4: 'Below Average', 5: 'Average', 6: 'Slightly Above Average', 7: 'Good', 8: 'Very Good', 9: 'Excellent', 10: 'Outstanding' };

  return (
    <div style={{ fontSize: '0.875rem' }}>
      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
        {[['Department', a.department], ['Cycle', a.cycle_name], ['Form', a.form_name]].map(([label, val]) => (
          <div key={label} style={{ flex: 1, minWidth: 100 }}>
            <p style={{ margin: 0, color: theme.textMuted, fontSize: '0.75rem' }}>{label}</p>
            <p style={{ margin: '0.1rem 0 0', fontWeight: 600 }}>{val || '—'}</p>
          </div>
        ))}
        <div style={{ flex: 1 }}>
          <p style={{ margin: 0, color: theme.textMuted, fontSize: '0.75rem' }}>Status</p>
          <div style={{ marginTop: '0.1rem' }}><Badge status={a.status} /></div>
        </div>
        {a.overall_employee_score && (
          <div style={{ flex: 1 }}>
            <p style={{ margin: 0, color: theme.textMuted, fontSize: '0.75rem' }}>Emp Score</p>
            <p style={{ margin: '0.1rem 0 0', fontWeight: 800, color: ratingColor(a.overall_employee_score) }}>{a.overall_employee_score}/10</p>
          </div>
        )}
        {a.overall_manager_score && (
          <div style={{ flex: 1 }}>
            <p style={{ margin: 0, color: theme.textMuted, fontSize: '0.75rem' }}>Mgr Score</p>
            <p style={{ margin: '0.1rem 0 0', fontWeight: 800, color: ratingColor(a.overall_manager_score) }}>{a.overall_manager_score}/10</p>
          </div>
        )}
      </div>
      {Object.entries(grouped).map(([section, responses]) => (
        <div key={section} style={{ marginBottom: '1rem' }}>
          <p style={{ margin: '0 0 0.5rem', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: theme.primary }}>{section}</p>
          {responses.map((r) => (
            <div key={r.id || r.field_id} style={{ padding: '0.625rem 0.75rem', background: '#F9FAFB', borderRadius: '0.5rem', marginBottom: '0.375rem' }}>
              <p style={{ margin: '0 0 0.375rem', fontWeight: 600, color: theme.text }}>{r.field_label}</p>
              <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
                {r.field_type === 'rating' ? (
                  <>
                    <div><span style={{ fontSize: '0.7rem', color: theme.textMuted }}>Self: </span><span style={{ fontWeight: 700, color: ratingColor(r.employee_rating) }}>{r.employee_rating ? `${r.employee_rating}/10 — ${RATING_LABELS[r.employee_rating]}` : '—'}</span></div>
                    <div><span style={{ fontSize: '0.7rem', color: theme.textMuted }}>Manager: </span><span style={{ fontWeight: 700, color: ratingColor(r.manager_rating) }}>{r.manager_rating ? `${r.manager_rating}/10 — ${RATING_LABELS[r.manager_rating]}` : '—'}</span></div>
                  </>
                ) : (
                  <p style={{ margin: 0, color: theme.text }}>{r.employee_comment || '—'}</p>
                )}
              </div>
              {r.employee_comment && r.field_type === 'rating' && (
                <p style={{ margin: '0.25rem 0 0', fontSize: '0.75rem', color: theme.textMuted, fontStyle: 'italic' }}>Note: {r.employee_comment}</p>
              )}
            </div>
          ))}
        </div>
      ))}
      {a.overall_manager_comment && (
        <div style={{ background: '#F0F9FF', border: '1px solid #BAE6FD', borderRadius: '0.5rem', padding: '0.75rem 1rem', marginTop: '0.5rem' }}>
          <p style={{ margin: '0 0 0.25rem', fontSize: '0.72rem', fontWeight: 700, color: '#0369A1' }}>MANAGER OVERALL COMMENT</p>
          <p style={{ margin: 0, color: theme.text }}>{a.overall_manager_comment}</p>
        </div>
      )}
    </div>
  );
}

// ─── FORM BUILDER TAB ─────────────────────────────────────────────────────────

function FormBuilder() {
  const [forms, setForms] = useState([]);
  const [selectedForm, setSelectedForm] = useState(null);
  const [formModal, setFormModal] = useState(null);
  const [formData, setFormData] = useState({ name: '', department: '', category: '', description: '' });
  const [fieldModal, setFieldModal] = useState(null);
  const [fieldData, setFieldData] = useState({ section: '', label: '', description: '', field_type: 'rating', sort_order: 0, is_required: true });
  const [saving, setSaving] = useState(false);

  const loadForms = useCallback(async () => {
    try {
      const r = await perfApi.getForms();
      setForms(r.data || []);
      if (r.data?.length && !selectedForm) setSelectedForm(r.data[0]);
    } catch {}
  }, [selectedForm]);

  useEffect(() => { loadForms(); }, [loadForms]);

  const saveForm = async () => {
    setSaving(true);
    try {
      if (formModal?.id) await perfApi.updateForm(formModal.id, formData);
      else await perfApi.createForm(formData);
      setFormModal(null);
      const r = await perfApi.getForms();
      setForms(r.data || []);
    } catch { alert('Failed to save form.'); } finally { setSaving(false); }
  };

  const deleteForm = async (id) => {
    if (!window.confirm('Delete this form template?')) return;
    try { await perfApi.deleteForm(id); setSelectedForm(null); loadForms(); } catch { alert('Failed.'); }
  };

  const saveField = async () => {
    setSaving(true);
    try {
      if (fieldModal?.id) await perfApi.updateField(selectedForm.id, fieldModal.id, fieldData);
      else await perfApi.addField(selectedForm.id, fieldData);
      setFieldModal(null);
      const r = await perfApi.getForms();
      const updated = (r.data || []).find(f => f.id === selectedForm.id);
      setForms(r.data || []);
      if (updated) setSelectedForm(updated);
    } catch { alert('Failed to save field.'); } finally { setSaving(false); }
  };

  const deleteField = async (fieldId) => {
    if (!window.confirm('Delete this field?')) return;
    try {
      await perfApi.deleteField(selectedForm.id, fieldId);
      const r = await perfApi.getForms();
      const updated = (r.data || []).find(f => f.id === selectedForm.id);
      setForms(r.data || []);
      if (updated) setSelectedForm(updated);
    } catch { alert('Failed.'); }
  };

  const groupedFields = (selectedForm?.fields || []).reduce((acc, f) => {
    const sec = f.section || 'General';
    if (!acc[sec]) acc[sec] = [];
    acc[sec].push(f);
    return acc;
  }, {});

  return (
    <div style={{ display: 'flex', gap: '1.25rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
      {/* Left: Form list */}
      <div style={{ width: 260, flexShrink: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
          <span style={{ fontWeight: 700, fontSize: '0.9rem', color: theme.text }}>Form Templates</span>
          <Button size="sm" variant="primary" onClick={() => { setFormModal({}); setFormData({ name: '', department: '', category: '', description: '' }); }}>+ New</Button>
        </div>
        {forms.length === 0 && <p style={{ fontSize: '0.8rem', color: theme.textMuted }}>No forms yet. Create one to get started.</p>}
        {forms.map((f) => (
          <div key={f.id} onClick={() => setSelectedForm(f)} style={{ ...card, padding: '0.875rem 1rem', marginBottom: '0.5rem', cursor: 'pointer', borderColor: selectedForm?.id === f.id ? theme.primary : theme.border, background: selectedForm?.id === f.id ? '#F0F4FF' : theme.white }}>
            <p style={{ margin: 0, fontWeight: 600, fontSize: '0.875rem', color: theme.text }}>{f.name}</p>
            {f.department && <p style={{ margin: '0.2rem 0 0', fontSize: '0.72rem', color: theme.textMuted }}>{f.department}{f.category ? ` › ${f.category}` : ''}</p>}
            <p style={{ margin: '0.3rem 0 0', fontSize: '0.7rem', color: theme.primary }}>{f.field_count} field{f.field_count !== 1 ? 's' : ''}</p>
          </div>
        ))}
      </div>

      {/* Right: Field editor */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {!selectedForm ? (
          <div style={{ ...card, textAlign: 'center', padding: '3rem', color: theme.textMuted }}>
            <p style={{ fontSize: '1.5rem', margin: '0 0 0.5rem' }}>📋</p>
            <p>Select a form template to edit its fields</p>
          </div>
        ) : (
          <div style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem', gap: '1rem', flexWrap: 'wrap' }}>
              <div>
                <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: theme.text }}>{selectedForm.name}</h2>
                {selectedForm.department && <p style={{ margin: '0.2rem 0 0', fontSize: '0.8rem', color: theme.textMuted }}>{selectedForm.department}{selectedForm.category ? ` › ${selectedForm.category}` : ''}</p>}
                {selectedForm.description && <p style={{ margin: '0.25rem 0 0', fontSize: '0.8rem', color: theme.textMuted }}>{selectedForm.description}</p>}
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                <Button size="sm" variant="outline" onClick={() => { setFormModal(selectedForm); setFormData({ name: selectedForm.name, department: selectedForm.department || '', category: selectedForm.category || '', description: selectedForm.description || '' }); }}>Edit</Button>
                <Button size="sm" variant="danger" onClick={() => deleteForm(selectedForm.id)}>Delete</Button>
                <Button size="sm" variant="primary" onClick={() => { setFieldModal({}); setFieldData({ section: '', label: '', description: '', field_type: 'rating', sort_order: (selectedForm.fields?.length || 0) * 10, is_required: true }); }}>+ Add Field</Button>
              </div>
            </div>

            {selectedForm.fields?.length === 0 && (
              <p style={{ color: theme.textMuted, fontSize: '0.875rem', textAlign: 'center', padding: '2rem' }}>No fields yet. Click "+ Add Field" to begin building the form.</p>
            )}

            {Object.entries(groupedFields).map(([section, fields]) => (
              <div key={section} style={{ marginBottom: '1.25rem' }}>
                <div style={{ fontSize: '0.72rem', fontWeight: 700, color: theme.primary, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.5rem', paddingBottom: '0.3rem', borderBottom: `1px solid ${theme.border}` }}>
                  {section}
                </div>
                {fields.map((f) => (
                  <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.625rem 0.75rem', borderRadius: '0.5rem', background: '#F9FAFB', marginBottom: '0.375rem' }}>
                    <span style={{ fontSize: '1rem' }}>{f.field_type === 'rating' ? '⭐' : '📝'}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ margin: 0, fontSize: '0.85rem', fontWeight: 600, color: theme.text }}>{f.label}</p>
                      {f.description && <p style={{ margin: '0.1rem 0 0', fontSize: '0.72rem', color: theme.textMuted }}>{f.description}</p>}
                    </div>
                    <div style={{ display: 'flex', gap: '0.375rem', alignItems: 'center', flexShrink: 0 }}>
                      <span style={{ fontSize: '0.65rem', color: theme.textMuted }}>#{f.sort_order}</span>
                      <span style={{ fontSize: '0.65rem', background: f.field_type === 'rating' ? '#DBEAFE' : '#EDE9FE', color: f.field_type === 'rating' ? '#1E40AF' : '#5B21B6', padding: '0.15rem 0.4rem', borderRadius: 4, fontWeight: 600 }}>{f.field_type}</span>
                      {f.is_required && <span style={{ fontSize: '0.65rem', color: '#EF4444', fontWeight: 700 }}>Required</span>}
                      <Button size="xs" variant="outline" onClick={() => { setFieldModal(f); setFieldData({ section: f.section || '', label: f.label, description: f.description || '', field_type: f.field_type, sort_order: f.sort_order, is_required: f.is_required }); }}>Edit</Button>
                      <Button size="xs" variant="danger" onClick={() => deleteField(f.id)}>✕</Button>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Form modal */}
      <Modal isOpen={!!formModal} onClose={() => setFormModal(null)} title={formModal?.id ? 'Edit Form Template' : 'New Form Template'}
        footer={<><Button variant="outlineGray" onClick={() => setFormModal(null)}>Cancel</Button><Button variant="primary" loading={saving} onClick={saveForm}>Save</Button></>}>
        <div style={fieldRow}><label style={lbl}>Form Name *</label><input style={inp} value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="e.g. Software Engineering 2025-26" /></div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.875rem' }}>
          <div>
            <label style={lbl}>Department</label>
            <select style={inp} value={formData.department} onChange={(e) => setFormData({ ...formData, department: e.target.value, category: '' })}>
              <option value="">All Departments</option>
              {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div>
            <label style={lbl}>Category</label>
            <select style={inp} value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })} disabled={!formData.department}>
              <option value="">All Categories</option>
              {(CATEGORIES[formData.department] || []).map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
        <div style={fieldRow}><label style={lbl}>Description</label><textarea style={{ ...inp, resize: 'vertical', minHeight: 60 }} value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} /></div>
      </Modal>

      {/* Field modal */}
      <Modal isOpen={!!fieldModal} onClose={() => setFieldModal(null)} title={fieldModal?.id ? 'Edit Field' : 'Add Field'}
        footer={<><Button variant="outlineGray" onClick={() => setFieldModal(null)}>Cancel</Button><Button variant="primary" loading={saving} onClick={saveField}>Save</Button></>}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.875rem' }}>
          <div><label style={lbl}>Section / Category</label><input style={inp} value={fieldData.section} onChange={(e) => setFieldData({ ...fieldData, section: e.target.value })} placeholder="e.g. Technical Skills" /></div>
          <div>
            <label style={lbl}>Field Type</label>
            <select style={inp} value={fieldData.field_type} onChange={(e) => setFieldData({ ...fieldData, field_type: e.target.value })}>
              <option value="rating">Rating (1–10)</option>
              <option value="text">Text / Comment</option>
            </select>
          </div>
        </div>
        <div style={fieldRow}><label style={lbl}>Field Label *</label><input style={inp} value={fieldData.label} onChange={(e) => setFieldData({ ...fieldData, label: e.target.value })} placeholder="e.g. Code Quality" /></div>
        <div style={fieldRow}><label style={lbl}>Help Text (optional)</label><input style={inp} value={fieldData.description} onChange={(e) => setFieldData({ ...fieldData, description: e.target.value })} placeholder="Brief guidance for the employee" /></div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
          <div><label style={lbl}>Sort Order</label><input style={inp} type="number" value={fieldData.sort_order} onChange={(e) => setFieldData({ ...fieldData, sort_order: Number(e.target.value) })} /></div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '1.4rem' }}>
            <input type="checkbox" id="req" checked={fieldData.is_required} onChange={(e) => setFieldData({ ...fieldData, is_required: e.target.checked })} />
            <label htmlFor="req" style={{ fontSize: '0.85rem', fontWeight: 600, color: theme.text }}>Required field</label>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ─── CYCLES TAB ───────────────────────────────────────────────────────────────

function CyclesSection({ forms }) {
  const [cycles, setCycles] = useState([]);
  const [cycleModal, setCycleModal] = useState(null);
  const [cycleData, setCycleData] = useState({ name: '', start_date: '', end_date: '', employee_deadline: '', manager_deadline: '' });
  const [launchModal, setLaunchModal] = useState(null);
  const [launchData, setLaunchData] = useState({ form_id: '', department_filter: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { const r = await perfApi.getCycles(); setCycles(r.data || []); } catch {}
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveCycle = async () => {
    setSaving(true);
    try { await perfApi.createCycle(cycleData); setCycleModal(null); load(); }
    catch { alert('Failed to save cycle.'); } finally { setSaving(false); }
  };

  const launchCycle = async () => {
    if (!launchData.form_id) { alert('Please select a form.'); return; }
    setSaving(true);
    try {
      const r = await perfApi.launchCycle(launchModal.id, { form_id: Number(launchData.form_id), department_filter: launchData.department_filter || null });
      alert(r.data.message);
      setLaunchModal(null);
      load();
    } catch (e) { alert(e.response?.data?.detail || 'Failed to launch.'); } finally { setSaving(false); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="primary" onClick={() => { setCycleModal({}); setCycleData({ name: '', start_date: '', end_date: '', employee_deadline: '', manager_deadline: '' }); }}>+ New Cycle</Button>
      </div>
      {cycles.length === 0 && <p style={{ color: theme.textMuted, textAlign: 'center', padding: '2rem' }}>No assessment cycles yet.</p>}
      {cycles.map((c) => (
        <div key={c.id} style={{ ...card, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <p style={{ margin: 0, fontWeight: 700, fontSize: '1rem', color: theme.text }}>{c.name}</p>
            <p style={{ margin: '0.2rem 0 0', fontSize: '0.78rem', color: theme.textMuted }}>
              {c.start_date} → {c.end_date}
              {c.employee_deadline && ` · Employee deadline: ${c.employee_deadline}`}
              {c.manager_deadline && ` · Manager deadline: ${c.manager_deadline}`}
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
            <CycleProgress submitted={c.submitted} reviewed={c.reviewed} total={c.total} />
            <Button size="sm" variant="primary" onClick={() => { setLaunchModal(c); setLaunchData({ form_id: forms[0]?.id || '', department_filter: '' }); }}>🚀 Launch</Button>
          </div>
        </div>
      ))}

      <Modal isOpen={!!cycleModal} onClose={() => setCycleModal(null)} title="New Assessment Cycle"
        footer={<><Button variant="outlineGray" onClick={() => setCycleModal(null)}>Cancel</Button><Button variant="primary" loading={saving} onClick={saveCycle}>Create</Button></>}>
        <div style={fieldRow}><label style={lbl}>Cycle Name *</label><input style={inp} value={cycleData.name} onChange={(e) => setCycleData({ ...cycleData, name: e.target.value })} placeholder="e.g. Annual Review 2025-26" /></div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.875rem' }}>
          <div><label style={lbl}>Start Date</label><input style={inp} type="date" value={cycleData.start_date} onChange={(e) => setCycleData({ ...cycleData, start_date: e.target.value })} /></div>
          <div><label style={lbl}>End Date</label><input style={inp} type="date" value={cycleData.end_date} onChange={(e) => setCycleData({ ...cycleData, end_date: e.target.value })} /></div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
          <div><label style={lbl}>Employee Submission Deadline</label><input style={inp} type="date" value={cycleData.employee_deadline} onChange={(e) => setCycleData({ ...cycleData, employee_deadline: e.target.value })} /></div>
          <div><label style={lbl}>Manager Review Deadline</label><input style={inp} type="date" value={cycleData.manager_deadline} onChange={(e) => setCycleData({ ...cycleData, manager_deadline: e.target.value })} /></div>
        </div>
      </Modal>

      <Modal isOpen={!!launchModal} onClose={() => setLaunchModal(null)} title={`Launch: ${launchModal?.name}`}
        footer={<><Button variant="outlineGray" onClick={() => setLaunchModal(null)}>Cancel</Button><Button variant="primary" loading={saving} onClick={launchCycle}>Launch Now</Button></>}>
        <p style={{ fontSize: '0.85rem', color: theme.textMuted, marginBottom: '1rem' }}>This will create a draft self-assessment for each active employee so they can fill and submit.</p>
        <div style={fieldRow}>
          <label style={lbl}>Assessment Form *</label>
          <SearchableSelect
            options={forms.map(f => ({ value: f.id, label: f.name, meta: f.department || '' }))}
            value={launchData.form_id}
            onChange={v => setLaunchData({ ...launchData, form_id: v })}
            placeholder="Select a form..."
          />
        </div>
        <div style={fieldRow}>
          <label style={lbl}>Filter by Department (optional)</label>
          <SearchableSelect
            options={[{ value: '', label: 'All Employees' }, ...DEPARTMENTS.map(d => ({ value: d, label: d }))]}
            value={launchData.department_filter}
            onChange={v => setLaunchData({ ...launchData, department_filter: v })}
            placeholder="All Employees"
          />
        </div>
      </Modal>
    </div>
  );
}

// ─── MONITOR TAB ──────────────────────────────────────────────────────────────

function MonitorSection() {
  const [cycles, setCycles] = useState([]);
  const [selectedCycle, setSelectedCycle] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [assessments, setAssessments] = useState([]);
  const [stats, setStats] = useState(null);
  const [detailModal, setDetailModal] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    perfApi.getCycles().then(r => setCycles(r.data || [])).catch(err => console.warn('[HRMS]', err?.message));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedCycle) params.cycle_id = selectedCycle;
      if (filterStatus) params.status = filterStatus;
      const [ar, sr] = await Promise.all([
        perfApi.getAllAssessments(params),
        perfApi.getStats(selectedCycle ? { cycle_id: selectedCycle } : {}),
      ]);
      setAssessments(ar.data || []);
      setStats(sr.data);
    } catch {} finally { setLoading(false); }
  }, [selectedCycle, filterStatus]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (a) => {
    try { const r = await perfApi.getAssessment(a.id); setDetailModal(r.data); } catch { setDetailModal(a); }
  };

  const scoreColor = (s) => {
    if (!s) return theme.textMuted;
    if (s >= 8) return '#10B981'; if (s >= 6) return '#3B82F6';
    if (s >= 4) return '#F59E0B'; return '#EF4444';
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {stats && (
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          {[
            { label: 'Total', value: stats.total, color: '#6B7280' },
            { label: 'Pending', value: stats.draft, color: '#F59E0B' },
            { label: 'Submitted', value: stats.submitted, color: '#3B82F6' },
            { label: 'Reviewed', value: stats.reviewed, color: '#10B981' },
            { label: 'Avg Emp Score', value: stats.avg_employee_score ? `${stats.avg_employee_score}/10` : '—', color: '#8B5CF6' },
            { label: 'Avg Mgr Score', value: stats.avg_manager_score ? `${stats.avg_manager_score}/10` : '—', color: '#EC4899' },
          ].map(({ label, value, color }) => (
            <div key={label} style={{ ...card, padding: '0.75rem 1rem', display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 90 }}>
              <span style={{ fontSize: '1.3rem', fontWeight: 800, color }}>{value}</span>
              <span style={{ fontSize: '0.68rem', color: theme.textMuted, marginTop: 2 }}>{label}</span>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <select style={{ ...inp, width: 220 }} value={selectedCycle} onChange={(e) => setSelectedCycle(e.target.value)}>
          <option value="">All Cycles</option>
          {cycles.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select style={{ ...inp, width: 160 }} value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
          <option value="">All Statuses</option>
          <option value="draft">Pending</option>
          <option value="submitted">Submitted</option>
          <option value="manager_reviewed">Reviewed</option>
        </select>
        <span style={{ fontSize: '0.8rem', color: theme.textMuted }}>{assessments.length} employee{assessments.length !== 1 ? 's' : ''}</span>
      </div>

      <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ background: '#F9FAFB', borderBottom: `1px solid ${theme.border}` }}>
                {['Employee', 'Department', 'Cycle', 'Status', 'Emp Score', 'Mgr Score', 'Submitted', 'Action'].map(h => (
                  <th key={h} style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: 600, color: theme.textMuted, fontSize: '0.78rem', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={8} style={{ padding: '2rem', textAlign: 'center', color: theme.textMuted }}>Loading...</td></tr>}
              {!loading && assessments.length === 0 && <tr><td colSpan={8} style={{ padding: '2rem', textAlign: 'center', color: theme.textMuted }}>No assessments found.</td></tr>}
              {assessments.map((a) => (
                <tr key={a.id} style={{ borderBottom: `1px solid ${theme.border}` }}>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    <p style={{ margin: 0, fontWeight: 600, color: theme.text }}>{a.employee_name}</p>
                    <p style={{ margin: 0, fontSize: '0.7rem', color: theme.textMuted }}>#{a.employee_code}</p>
                  </td>
                  <td style={{ padding: '0.75rem 1rem', color: theme.textMuted, fontSize: '0.8rem' }}>{a.department || '—'}</td>
                  <td style={{ padding: '0.75rem 1rem', color: theme.textMuted, fontSize: '0.8rem' }}>{a.cycle_name || '—'}</td>
                  <td style={{ padding: '0.75rem 1rem' }}><Badge status={a.status} /></td>
                  <td style={{ padding: '0.75rem 1rem', fontWeight: 700, color: scoreColor(a.overall_employee_score) }}>{a.overall_employee_score ? `${a.overall_employee_score}/10` : '—'}</td>
                  <td style={{ padding: '0.75rem 1rem', fontWeight: 700, color: scoreColor(a.overall_manager_score) }}>{a.overall_manager_score ? `${a.overall_manager_score}/10` : '—'}</td>
                  <td style={{ padding: '0.75rem 1rem', color: theme.textMuted, fontSize: '0.78rem' }}>{a.employee_submitted_at ? new Date(a.employee_submitted_at).toLocaleDateString() : '—'}</td>
                  <td style={{ padding: '0.75rem 1rem' }}><Button size="sm" variant="outline" onClick={() => openDetail(a)}>View</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Modal isOpen={!!detailModal} onClose={() => setDetailModal(null)} title={`Assessment: ${detailModal?.employee_name}`}
        footer={<Button variant="outlineGray" onClick={() => setDetailModal(null)}>Close</Button>}>
        {detailModal && <AssessmentDetailView a={detailModal} />}
      </Modal>
    </div>
  );
}

// ─── GOAL MANAGEMENT TAB ──────────────────────────────────────────────────────

function GoalManagementTab() {
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterLevel, setFilterLevel] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [editGoal, setEditGoal] = useState(null);
  const [goalForm, setGoalForm] = useState({ title: '', description: '', goal_type: 'okr', level: 'organization', cycle_type: 'quarterly', year: new Date().getFullYear(), quarter: 1, target_value: '', unit: '', weight: '', due_date: '', status: 'active' });
  const [keyResults, setKeyResults] = useState([]);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (filterLevel) params.level = filterLevel;
    if (filterStatus) params.status = filterStatus;
    if (filterType) params.goal_type = filterType;
    perfApi.getGoals(params).then(r => { setGoals(r.data || []); setLoading(false); }).catch(() => setLoading(false));
  }, [filterLevel, filterStatus, filterType]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setGoalForm({ title: '', description: '', goal_type: 'okr', level: 'organization', cycle_type: 'quarterly', year: new Date().getFullYear(), quarter: 1, target_value: '', unit: '', weight: '', due_date: '', status: 'active' });
    setKeyResults([]); setEditGoal(null); setFormError(''); setShowCreate(true);
  };

  const openEdit = (g) => {
    setGoalForm({ title: g.title, description: g.description || '', goal_type: g.goal_type, level: g.level, cycle_type: g.cycle_type, year: g.year, quarter: g.quarter || 1, target_value: g.target_value || '', unit: g.unit || '', weight: g.weight || '', due_date: g.due_date || '', status: g.status });
    setKeyResults(g.key_results || []); setEditGoal(g); setFormError(''); setShowCreate(true);
  };

  const saveGoal = async () => {
    if (!goalForm.title.trim()) { setFormError('Title is required'); return; }
    setSaving(true); setFormError('');
    try {
      const payload = { ...goalForm, target_value: goalForm.target_value ? parseFloat(goalForm.target_value) : null, weight: goalForm.weight ? parseFloat(goalForm.weight) : null, key_results: keyResults };
      if (editGoal) await perfApi.updateGoal(editGoal.id, payload);
      else await perfApi.createGoal(payload);
      setShowCreate(false); load();
    } catch (e) { setFormError(e.response?.data?.detail || 'Failed to save goal'); }
    finally { setSaving(false); }
  };

  const deleteGoal = async (id) => {
    if (!window.confirm('Delete this goal?')) return;
    try { await perfApi.deleteGoal(id); load(); } catch (e) { alert(e.response?.data?.detail || 'Failed to delete'); }
  };

  const addKR = () => setKeyResults(prev => [...prev, { title: '', target_value: '', current_value: 0, unit: '' }]);
  const updateKR = (i, k, v) => setKeyResults(prev => prev.map((kr, idx) => idx === i ? { ...kr, [k]: v } : kr));
  const removeKR = (i) => setKeyResults(prev => prev.filter((_, idx) => idx !== i));

  const byLevel = { organization: 0, department: 0, individual: 0 };
  goals.forEach(g => { if (byLevel[g.level] !== undefined) byLevel[g.level]++; });
  const levelColors = { organization: '#7C3AED', department: '#0891B2', individual: '#059669' };

  return (
    <div>
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1.25rem' }}>
        <StatCard label="Total Goals" value={goals.length} color="#3B82F6" />
        <StatCard label="Organization" value={byLevel.organization} color="#7C3AED" />
        <StatCard label="Department" value={byLevel.department} color="#0891B2" />
        <StatCard label="Individual" value={byLevel.individual} color="#059669" />
        <StatCard label="Active" value={goals.filter(g => ['active', 'on_track'].includes(g.status)).length} color="#F59E0B" />
      </div>

      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <select value={filterLevel} onChange={e => setFilterLevel(e.target.value)} style={{ ...inp, width: 150 }}>
          <option value="">All Levels</option>
          <option value="organization">Organization</option>
          <option value="department">Department</option>
          <option value="individual">Individual</option>
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ ...inp, width: 150 }}>
          <option value="">All Statuses</option>
          {['draft', 'active', 'on_track', 'at_risk', 'completed', 'cancelled'].map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
        </select>
        <select value={filterType} onChange={e => setFilterType(e.target.value)} style={{ ...inp, width: 120 }}>
          <option value="">All Types</option>
          <option value="okr">OKR</option>
          <option value="kpi">KPI</option>
        </select>
        <Button variant="primary" onClick={openCreate} style={{ marginLeft: 'auto' }}>+ Create Goal</Button>
      </div>

      {loading ? <div style={{ textAlign: 'center', padding: '2rem', color: theme.textMuted }}>Loading...</div> :
        goals.length === 0 ? <div style={{ ...card, textAlign: 'center', padding: '3rem', color: theme.textMuted }}>No goals found</div> : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {goals.map(g => {
              const pct = g.progress_pct ?? 0;
              const progressColor = pct >= 80 ? '#10B981' : pct >= 50 ? '#3B82F6' : pct >= 25 ? '#F59E0B' : '#EF4444';
              return (
                <div key={g.id} style={card}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.3rem' }}>
                        <span style={{ fontSize: '0.7rem', fontWeight: 700, padding: '0.15rem 0.5rem', borderRadius: 999, background: g.goal_type === 'okr' ? '#EDE9FE' : '#DBEAFE', color: g.goal_type === 'okr' ? '#7C3AED' : '#1D4ED8', textTransform: 'uppercase' }}>{g.goal_type}</span>
                        <span style={{ fontSize: '0.7rem', fontWeight: 700, padding: '0.15rem 0.5rem', borderRadius: 999, background: `${levelColors[g.level]}22`, color: levelColors[g.level], textTransform: 'capitalize' }}>{g.level}</span>
                        <GoalStatusBadge status={g.status} />
                        {g.weight && <span style={{ fontSize: '0.7rem', color: theme.textMuted }}>• {g.weight}% weight</span>}
                      </div>
                      <p style={{ margin: '0 0 0.2rem', fontWeight: 700, color: theme.text }}>{g.title}</p>
                      {g.description && <p style={{ margin: 0, fontSize: '0.8rem', color: theme.textMuted }}>{g.description}</p>}
                      {g.due_date && <p style={{ margin: '0.25rem 0 0', fontSize: '0.72rem', color: theme.textMuted }}>Due: {fmtDate(g.due_date)}</p>}
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontSize: '1.3rem', fontWeight: 700, color: progressColor }}>{pct}%</div>
                      <div style={{ fontSize: '0.7rem', color: theme.textMuted }}>progress</div>
                      <div style={{ display: 'flex', gap: '0.4rem', marginTop: '0.5rem', justifyContent: 'flex-end' }}>
                        <Button size="xs" variant="outline" onClick={() => openEdit(g)}>Edit</Button>
                        <Button size="xs" variant="danger" onClick={() => deleteGoal(g.id)}>Delete</Button>
                      </div>
                    </div>
                  </div>
                  <div style={{ marginTop: '0.75rem' }}>
                    <ProgressBar value={pct} color={progressColor} />
                    {g.target_value && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.25rem', fontSize: '0.72rem', color: theme.textMuted }}>
                        <span>Current: {g.current_value ?? 0} {g.unit}</span>
                        <span>Target: {g.target_value} {g.unit}</span>
                      </div>
                    )}
                  </div>
                  {(g.key_results?.length > 0 || g.checkins_count > 0) && (
                    <div style={{ marginTop: '0.4rem', fontSize: '0.75rem', color: theme.textMuted }}>
                      {g.key_results?.length > 0 && <>{g.key_results.length} KR{g.key_results.length > 1 ? 's' : ''}</>}
                      {g.checkins_count > 0 && <> • {g.checkins_count} check-in{g.checkins_count !== 1 ? 's' : ''}</>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )
      }

      {/* Create/Edit Goal Modal */}
      <Modal isOpen={showCreate} onClose={() => setShowCreate(false)} title={editGoal ? 'Edit Goal' : 'Create Goal'}
        footer={<><Button variant="outlineGray" onClick={() => setShowCreate(false)}>Cancel</Button><Button variant="primary" loading={saving} onClick={saveGoal}>{editGoal ? 'Update Goal' : 'Create Goal'}</Button></>}>
        {formError && <div style={{ background: '#FEE2E2', color: '#991B1B', padding: '0.6rem 1rem', borderRadius: '0.5rem', marginBottom: '1rem', fontSize: '0.85rem' }}>{formError}</div>}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.875rem' }}>
          <div style={{ gridColumn: '1/-1' }}>
            <label style={lbl}>Title *</label>
            <input style={inp} value={goalForm.title} onChange={e => setGoalForm(f => ({ ...f, title: e.target.value }))} placeholder="Goal title" />
          </div>
          <div style={{ gridColumn: '1/-1' }}>
            <label style={lbl}>Description</label>
            <textarea style={{ ...inp, resize: 'vertical', minHeight: 65 }} value={goalForm.description} onChange={e => setGoalForm(f => ({ ...f, description: e.target.value }))} placeholder="What does success look like?" />
          </div>
          <div><label style={lbl}>Goal Type</label>
            <select style={inp} value={goalForm.goal_type} onChange={e => setGoalForm(f => ({ ...f, goal_type: e.target.value }))}>
              <option value="okr">OKR</option><option value="kpi">KPI</option>
            </select>
          </div>
          <div><label style={lbl}>Level</label>
            <select style={inp} value={goalForm.level} onChange={e => setGoalForm(f => ({ ...f, level: e.target.value }))}>
              <option value="organization">Organization</option><option value="department">Department</option><option value="individual">Individual</option>
            </select>
          </div>
          <div><label style={lbl}>Review Cycle</label>
            <select style={inp} value={goalForm.cycle_type} onChange={e => setGoalForm(f => ({ ...f, cycle_type: e.target.value }))}>
              <option value="quarterly">Quarterly</option><option value="half_yearly">Half Yearly</option><option value="annual">Annual</option><option value="custom">Custom</option>
            </select>
          </div>
          <div><label style={lbl}>Status</label>
            <select style={inp} value={goalForm.status} onChange={e => setGoalForm(f => ({ ...f, status: e.target.value }))}>
              {['draft', 'active', 'on_track', 'at_risk', 'completed', 'cancelled'].map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
            </select>
          </div>
          <div><label style={lbl}>Year</label>
            <input type="number" style={inp} value={goalForm.year} onChange={e => setGoalForm(f => ({ ...f, year: parseInt(e.target.value) }))} />
          </div>
          {goalForm.cycle_type === 'quarterly' && (
            <div><label style={lbl}>Quarter</label>
              <select style={inp} value={goalForm.quarter} onChange={e => setGoalForm(f => ({ ...f, quarter: parseInt(e.target.value) }))}>
                <option value={1}>Q1</option><option value={2}>Q2</option><option value={3}>Q3</option><option value={4}>Q4</option>
              </select>
            </div>
          )}
          <div><label style={lbl}>Target Value</label>
            <input type="number" style={inp} value={goalForm.target_value} onChange={e => setGoalForm(f => ({ ...f, target_value: e.target.value }))} placeholder="e.g. 100" />
          </div>
          <div><label style={lbl}>Unit</label>
            <input style={inp} value={goalForm.unit} onChange={e => setGoalForm(f => ({ ...f, unit: e.target.value }))} placeholder="%, deals, pts..." />
          </div>
          <div><label style={lbl}>Weight (%)</label>
            <input type="number" style={inp} value={goalForm.weight} onChange={e => setGoalForm(f => ({ ...f, weight: e.target.value }))} placeholder="e.g. 40" />
          </div>
          <div><label style={lbl}>Due Date</label>
            <input type="date" style={inp} value={goalForm.due_date} onChange={e => setGoalForm(f => ({ ...f, due_date: e.target.value }))} />
          </div>
        </div>
        {goalForm.goal_type === 'okr' && (
          <div style={{ marginTop: '1.25rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
              <span style={{ fontWeight: 700, fontSize: '0.9rem', color: theme.text }}>Key Results</span>
              <Button size="sm" variant="outline" onClick={addKR}>+ Add KR</Button>
            </div>
            {keyResults.map((kr, i) => (
              <div key={i} style={{ background: '#F9FAFB', borderRadius: '0.5rem', padding: '0.75rem', marginBottom: '0.5rem', display: 'grid', gridTemplateColumns: '1fr 100px 80px auto', gap: '0.5rem', alignItems: 'center' }}>
                <input style={inp} value={kr.title} onChange={e => updateKR(i, 'title', e.target.value)} placeholder="Key result title" />
                <input type="number" style={inp} value={kr.target_value} onChange={e => updateKR(i, 'target_value', e.target.value)} placeholder="Target" />
                <input style={inp} value={kr.unit} onChange={e => updateKR(i, 'unit', e.target.value)} placeholder="Unit" />
                <Button size="xs" variant="danger" onClick={() => removeKR(i)}>✕</Button>
              </div>
            ))}
          </div>
        )}
      </Modal>
    </div>
  );
}

// ─── COMPETENCY FRAMEWORK TAB ─────────────────────────────────────────────────

function CompetencyFrameworkTab() {
  const [competencies, setCompetencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editComp, setEditComp] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', department: '', category: '', expected_level: 'proficient' });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [rateModal, setRateModal] = useState(null);
  const [rateForm, setRateForm] = useState({ employee_id: '', self_level: 'proficient', manager_level: 'proficient', notes: '' });
  const [rateSaving, setRateSaving] = useState(false);

  const load = () => {
    setLoading(true);
    perfApi.getCompetencies().then(r => { setCompetencies(r.data || []); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm({ name: '', description: '', department: '', category: '', expected_level: 'proficient' }); setEditComp(null); setFormError(''); setShowForm(true); };
  const openEdit = (c) => { setForm({ name: c.name, description: c.description || '', department: c.department || '', category: c.category || '', expected_level: c.expected_level || 'proficient' }); setEditComp(c); setFormError(''); setShowForm(true); };

  const save = async () => {
    if (!form.name.trim()) { setFormError('Name is required'); return; }
    setSaving(true); setFormError('');
    try {
      if (editComp) await perfApi.updateCompetency(editComp.id, form);
      else await perfApi.createCompetency(form);
      setShowForm(false); load();
    } catch (e) { setFormError(e.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const deleteComp = async (id) => {
    if (!window.confirm('Delete this competency?')) return;
    try { await perfApi.deleteCompetency(id); load(); } catch (e) { alert(e.response?.data?.detail || 'Failed to delete'); }
  };

  const saveRating = async () => {
    if (!rateModal || !rateForm.employee_id) { alert('Employee ID required'); return; }
    setRateSaving(true);
    try {
      await perfApi.rateCompetency({ competency_id: rateModal.id, employee_id: parseInt(rateForm.employee_id), self_level: rateForm.self_level, manager_level: rateForm.manager_level, notes: rateForm.notes });
      setRateModal(null);
    } catch (e) { alert(e.response?.data?.detail || 'Failed to rate'); }
    finally { setRateSaving(false); }
  };

  // Group competencies by Department > Category
  const grouped = {};
  competencies.forEach(c => {
    const dept = c.department || 'General';
    const cat = c.category || 'Uncategorized';
    const key = `${dept}|||${cat}`;
    if (!grouped[key]) grouped[key] = { dept, cat, items: [] };
    grouped[key].items.push(c);
  });
  const deptCount = [...new Set(competencies.map(c => c.department).filter(Boolean))].length;
  const catCount = [...new Set(competencies.map(c => c.category).filter(Boolean))].length;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          <StatCard label="Total Competencies" value={competencies.length} color="#7C3AED" />
          <StatCard label="Departments" value={deptCount} color="#0891B2" />
          <StatCard label="Categories" value={catCount} color="#059669" />
        </div>
        <Button variant="primary" onClick={openCreate}>+ Add Competency</Button>
      </div>

      {loading ? <div style={{ textAlign: 'center', padding: '2rem', color: theme.textMuted }}>Loading...</div> :
        competencies.length === 0 ? <div style={{ ...card, textAlign: 'center', padding: '3rem', color: theme.textMuted }}>No competencies defined yet</div> : (
          <div>
            {Object.values(grouped).map(({ dept, cat, items }) => (
              <div key={`${dept}-${cat}`} style={{ marginBottom: '1.5rem' }}>
                {/* Dept > Category header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                  <span style={{ fontSize: '0.72rem', fontWeight: 700, padding: '0.2rem 0.6rem', borderRadius: 999, background: '#EDE9FE', color: '#7C3AED', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{dept}</span>
                  <span style={{ color: theme.textMuted, fontSize: '0.8rem' }}>›</span>
                  <span style={{ fontSize: '0.85rem', fontWeight: 700, color: theme.text }}>{cat}</span>
                  <span style={{ fontSize: '0.72rem', color: theme.textMuted }}>({items.length})</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '0.75rem' }}>
                  {items.map(c => {
                    const expIdx = COMPETENCY_LEVELS.indexOf(c.expected_level || 'proficient');
                    return (
                      <div key={c.id} style={card}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                          <p style={{ margin: 0, fontWeight: 700, color: theme.text, fontSize: '0.9rem', flex: 1 }}>{c.name}</p>
                          <div style={{ display: 'flex', gap: '0.35rem', flexShrink: 0, marginLeft: '0.5rem' }}>
                            <Button size="xs" variant="outline" onClick={() => { setRateForm({ employee_id: '', self_level: 'proficient', manager_level: c.expected_level || 'proficient', notes: '' }); setRateModal(c); }}>Rate</Button>
                            <Button size="xs" variant="outline" onClick={() => openEdit(c)}>Edit</Button>
                            <Button size="xs" variant="danger" onClick={() => deleteComp(c.id)}>✕</Button>
                          </div>
                        </div>
                        {c.description && <p style={{ margin: '0 0 0.6rem', fontSize: '0.78rem', color: theme.textMuted }}>{c.description}</p>}
                        <div style={{ fontSize: '0.72rem', color: theme.textMuted, marginBottom: '0.3rem' }}>
                          Expected: <strong style={{ color: COMPETENCY_COLORS[c.expected_level] || '#6B7280', textTransform: 'capitalize' }}>{c.expected_level || 'proficient'}</strong>
                        </div>
                        <div style={{ display: 'flex', gap: '0.3rem' }}>
                          {COMPETENCY_LEVELS.map((lvl, i) => (
                            <div key={lvl} style={{ flex: 1, height: 6, borderRadius: 3, background: i <= expIdx ? COMPETENCY_COLORS[lvl] : '#E5E7EB' }} title={lvl} />
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )
      }

      <Modal isOpen={showForm} onClose={() => setShowForm(false)} title={editComp ? 'Edit Competency' : 'Add Competency'}
        footer={<><Button variant="outlineGray" onClick={() => setShowForm(false)}>Cancel</Button><Button variant="primary" loading={saving} onClick={save}>{editComp ? 'Update' : 'Add Competency'}</Button></>}>
        {formError && <div style={{ background: '#FEE2E2', color: '#991B1B', padding: '0.6rem 1rem', borderRadius: '0.5rem', marginBottom: '1rem', fontSize: '0.85rem' }}>{formError}</div>}
        <div style={fieldRow}><label style={lbl}>Name *</label><input style={inp} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Code Quality, Communication" /></div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.875rem' }}>
          <div>
            <label style={lbl}>Department</label>
            <select style={inp} value={form.department} onChange={e => setForm(f => ({ ...f, department: e.target.value, category: '' }))}>
              <option value="">All Departments</option>
              {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div>
            <label style={lbl}>Category</label>
            <select style={inp} value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} disabled={!form.department}>
              <option value="">Select category...</option>
              {(CATEGORIES[form.department] || []).map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
        <div style={fieldRow}><label style={lbl}>Description</label><textarea style={{ ...inp, resize: 'vertical', minHeight: 65 }} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="What this competency entails..." /></div>
        <div style={fieldRow}><label style={lbl}>Expected Level</label>
          <select style={inp} value={form.expected_level} onChange={e => setForm(f => ({ ...f, expected_level: e.target.value }))}>
            {COMPETENCY_LEVELS.map(l => <option key={l} value={l}>{l.charAt(0).toUpperCase() + l.slice(1)}</option>)}
          </select>
        </div>
      </Modal>

      <Modal isOpen={!!rateModal} onClose={() => setRateModal(null)} title={`Rate Employee: ${rateModal?.name}`}
        footer={<><Button variant="outlineGray" onClick={() => setRateModal(null)}>Cancel</Button><Button variant="primary" loading={rateSaving} onClick={saveRating}>Save Rating</Button></>}>
        <div style={fieldRow}><label style={lbl}>Employee ID</label><input type="number" style={inp} value={rateForm.employee_id} onChange={e => setRateForm(f => ({ ...f, employee_id: e.target.value }))} placeholder="Enter employee ID" /></div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.875rem', marginBottom: '0.875rem' }}>
          <div><label style={lbl}>Self Assessment Level</label>
            <select style={inp} value={rateForm.self_level} onChange={e => setRateForm(f => ({ ...f, self_level: e.target.value }))}>
              {COMPETENCY_LEVELS.map(l => <option key={l} value={l}>{l.charAt(0).toUpperCase() + l.slice(1)}</option>)}
            </select>
          </div>
          <div><label style={lbl}>Manager Assessment Level</label>
            <select style={inp} value={rateForm.manager_level} onChange={e => setRateForm(f => ({ ...f, manager_level: e.target.value }))}>
              {COMPETENCY_LEVELS.map(l => <option key={l} value={l}>{l.charAt(0).toUpperCase() + l.slice(1)}</option>)}
            </select>
          </div>
        </div>
        <div style={fieldRow}><label style={lbl}>Notes</label><textarea style={{ ...inp, resize: 'vertical', minHeight: 65 }} value={rateForm.notes} onChange={e => setRateForm(f => ({ ...f, notes: e.target.value }))} placeholder="Optional notes..." /></div>
      </Modal>
    </div>
  );
}

// ─── ANALYTICS DASHBOARD TAB ──────────────────────────────────────────────────

function AnalyticsDashboardTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());
  const [quarter, setQuarter] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    const params = { year };
    if (quarter) params.quarter = quarter;
    perfApi.getAnalyticsOverview(params).then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [year, quarter]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div style={{ textAlign: 'center', padding: '2rem', color: theme.textMuted }}>Loading analytics...</div>;
  if (!data) return <div style={{ textAlign: 'center', padding: '2rem', color: theme.textMuted }}>No analytics data available</div>;

  const statusData = data.goal_status_distribution || {};
  const totalGoals = Object.values(statusData).reduce((a, b) => a + b, 0);
  const goalStatusColors = { active: '#3B82F6', on_track: '#10B981', at_risk: '#F59E0B', completed: '#8B5CF6', draft: '#9CA3AF', cancelled: '#EF4444' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <select value={year} onChange={e => setYear(parseInt(e.target.value))} style={{ ...inp, width: 110 }}>
          {[2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <select value={quarter} onChange={e => setQuarter(e.target.value)} style={{ ...inp, width: 130 }}>
          <option value="">All Quarters</option>
          <option value="1">Q1</option><option value="2">Q2</option><option value="3">Q3</option><option value="4">Q4</option>
        </select>
      </div>

      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
        <StatCard label="Total Employees" value={data.total_employees ?? 0} color="#3B82F6" />
        <StatCard label="Active Goals" value={data.active_goals ?? 0} color="#8B5CF6" />
        <StatCard label="Completed Goals" value={data.completed_goals ?? 0} color="#10B981" />
        <StatCard label="At Risk" value={data.at_risk_goals ?? 0} color="#F59E0B" />
        <StatCard label="Avg Progress" value={`${data.avg_progress ?? 0}%`} color="#0EA5E9" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }}>
        <div style={card}>
          <h3 style={{ margin: '0 0 1rem', fontSize: '0.95rem', fontWeight: 700, color: theme.text }}>Goal Status Distribution</h3>
          {totalGoals === 0 ? <div style={{ color: theme.textMuted, fontSize: '0.85rem' }}>No goal data</div> : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
              {Object.entries(statusData).map(([status, count]) => {
                const pct = Math.round((count / totalGoals) * 100);
                const color = goalStatusColors[status] || '#9CA3AF';
                return (
                  <div key={status}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', marginBottom: '0.25rem' }}>
                      <span style={{ textTransform: 'capitalize', color: theme.text, fontWeight: 600 }}>{status.replace('_', ' ')}</span>
                      <span style={{ color, fontWeight: 700 }}>{count} ({pct}%)</span>
                    </div>
                    <ProgressBar value={pct} color={color} />
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div style={card}>
          <h3 style={{ margin: '0 0 1rem', fontSize: '0.95rem', fontWeight: 700, color: theme.text }}>Department Averages</h3>
          {!data.dept_averages?.length ? <div style={{ color: theme.textMuted, fontSize: '0.85rem' }}>No department data</div> : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
              {data.dept_averages.map((d, i) => {
                const pct = d.avg_progress ?? 0;
                const color = pct >= 75 ? '#10B981' : pct >= 50 ? '#3B82F6' : '#F59E0B';
                return (
                  <div key={i}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', marginBottom: '0.25rem' }}>
                      <span style={{ color: theme.text, fontWeight: 600 }}>{d.department || 'Unassigned'}</span>
                      <span style={{ color, fontWeight: 700 }}>{pct}%</span>
                    </div>
                    <ProgressBar value={pct} color={color} />
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div style={card}>
          <h3 style={{ margin: '0 0 1rem', fontSize: '0.95rem', fontWeight: 700, color: theme.text }}>Top Performers</h3>
          {!data.top_performers?.length ? <div style={{ color: theme.textMuted, fontSize: '0.85rem' }}>No data yet</div> : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
              {data.top_performers.map((p, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <div style={{ width: 24, height: 24, borderRadius: '50%', background: ['#F59E0B', '#9CA3AF', '#CD7F32'][i] || '#E5E7EB', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 700, color: '#fff', flexShrink: 0 }}>{i + 1}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '0.85rem', fontWeight: 600, color: theme.text }}>{p.name}</div>
                    <div style={{ fontSize: '0.72rem', color: theme.textMuted }}>{p.department}</div>
                  </div>
                  <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#10B981' }}>{p.avg_progress ?? 0}%</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={card}>
          <h3 style={{ margin: '0 0 1rem', fontSize: '0.95rem', fontWeight: 700, color: theme.text }}>Needs Attention</h3>
          {!data.low_performers?.length ? <div style={{ color: theme.textMuted, fontSize: '0.85rem' }}>No at-risk employees</div> : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
              {data.low_performers.map((p, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <span style={{ fontSize: '1rem' }}>⚠️</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '0.85rem', fontWeight: 600, color: theme.text }}>{p.name}</div>
                    <div style={{ fontSize: '0.72rem', color: theme.textMuted }}>{p.department}</div>
                  </div>
                  <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#F59E0B' }}>{p.avg_progress ?? 0}%</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function Performance() {
  const [activeTab, setActiveTab] = useState('Form Builder');
  const [forms, setForms] = useState([]);

  useEffect(() => {
    perfApi.getForms().then(r => setForms(r.data || [])).catch(err => console.warn('[HRMS]', err?.message));
  }, []);

  const tabs = [
    { key: 'Form Builder', label: 'Form Builder' },
    { key: 'Cycles', label: 'Cycles' },
    { key: 'Monitor', label: 'Monitor' },
    { key: 'Goals', label: 'Goal Management' },
    { key: 'Competencies', label: 'Competency Framework' },
    { key: 'Analytics', label: 'Analytics' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: theme.text, margin: 0 }}>Performance Management</h1>
        <p style={{ color: theme.textMuted, fontSize: '0.875rem', margin: '0.25rem 0 0' }}>Assessment forms, cycles, OKRs, competencies and analytics</p>
      </div>

      <div style={{ display: 'flex', gap: '0.25rem', borderBottom: `2px solid ${theme.border}`, flexWrap: 'wrap' }}>
        {tabs.map((t) => (
          <button key={t.key} onClick={() => setActiveTab(t.key)} style={{
            padding: '0.625rem 1.1rem', background: 'none', border: 'none', cursor: 'pointer',
            fontWeight: activeTab === t.key ? 700 : 400,
            color: activeTab === t.key ? theme.primary : theme.textMuted,
            borderBottom: activeTab === t.key ? `2px solid ${theme.primary}` : '2px solid transparent',
            marginBottom: '-2px', fontSize: '0.875rem', whiteSpace: 'nowrap',
          }}>{t.label}</button>
        ))}
      </div>

      {activeTab === 'Form Builder' && <FormBuilder />}
      {activeTab === 'Cycles' && <CyclesSection forms={forms} />}
      {activeTab === 'Monitor' && <MonitorSection />}
      {activeTab === 'Goals' && <GoalManagementTab />}
      {activeTab === 'Competencies' && <CompetencyFrameworkTab />}
      {activeTab === 'Analytics' && <AnalyticsDashboardTab />}
    </div>
  );
}
